/**
 * @file Skeleton.tsx
 * @description Animated placeholder primitives shown while data loads.
 *   `Skeleton` is a single shimmering rectangle; `SkeletonRows` renders N
 *   list-row-shaped placeholders for lists. The animate-pulse class is
 *   Tailwind built-in; we just compose a couple of greys + the right
 *   sizing so the placeholder roughly matches the post-load layout
 *   (avoids the jarring jump when content arrives).
 * @module Components/Common
 * @security `aria-hidden` so screen readers don't announce the
 *   shimmer; the surrounding container should keep an `aria-busy` or
 *   the page's normal heading audible.
 */

import type { JSX } from 'react';

interface SkeletonProps {
  /** Tailwind size/shape classes — e.g. "h-4 w-32" or "h-12 w-12 rounded-full". */
  className?: string;
}

export function Skeleton({ className = 'h-4 w-full' }: SkeletonProps): JSX.Element {
  return (
    <div aria-hidden="true" className={`animate-pulse rounded-lg bg-slate-200/70 ${className}`} />
  );
}

interface SkeletonRowsProps {
  /** How many rows to render. Defaults to 3 — usually enough to fill above-the-fold. */
  count?: number;
  /** Per-row height (Tailwind class). */
  rowClassName?: string;
}

export function SkeletonRows({
  count = 3,
  rowClassName = 'h-16 w-full',
}: SkeletonRowsProps): JSX.Element {
  const rows: number[] = [];
  for (let i = 0; i < count; i++) rows.push(i);
  return (
    <ul aria-busy="true" className="space-y-2">
      {rows.map((i) => (
        <li key={i}>
          <Skeleton className={rowClassName} />
        </li>
      ))}
    </ul>
  );
}
